import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Seeded random for consistent but organic placement
function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

// Organic scattered layout - no circles
function layoutNodes(themes, emotions, width, height, padding) {
  const nodes = [];
  const total = themes.length + emotions.length;
  if (total === 0) return nodes;

  const rng = seededRandom(42);
  const placed = [];
  const minDist = 65; // minimum distance between nodes

  const tryPlace = (item, type, zone) => {
    // zone: inner area for themes, outer for emotions
    const innerX = padding + 40;
    const innerY = padding + 30;
    const outerX = width - padding - 40;
    const outerY = height - padding - 30;

    for (let attempt = 0; attempt < 80; attempt++) {
      let x, y;
      if (zone === 'inner') {
        // Themes: cluster loosely around center
        x = width * 0.25 + rng() * width * 0.5;
        y = height * 0.2 + rng() * height * 0.55;
      } else {
        // Emotions: scatter more widely
        x = innerX + rng() * (outerX - innerX);
        y = innerY + rng() * (outerY - innerY);
      }

      // Check distance from all placed nodes
      const tooClose = placed.some(p =>
        Math.sqrt((p.x - x) ** 2 + (p.y - y) ** 2) < minDist
      );

      if (!tooClose) {
        placed.push({ x, y });
        nodes.push({ ...item, type, x, y });
        return;
      }
    }
    // Fallback: place anyway with slight offset
    const x = padding + rng() * (width - padding * 2);
    const y = padding + rng() * (height - padding * 2);
    placed.push({ x, y });
    nodes.push({ ...item, type, x, y });
  };

  themes.forEach(t => tryPlace(t, 'theme', 'inner'));
  emotions.forEach(e => tryPlace(e, 'emotion', 'outer'));

  return nodes;
}

const STARS = Array.from({ length: 30 }, () => ({
  x: Math.random() * 100,
  y: Math.random() * 100,
  r: Math.random() * 1 + 0.3,
  opacity: Math.random() * 0.4 + 0.15,
  delay: Math.random() * 5,
}));

export default function PersonalDreamMap() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeNode, setActiveNode] = useState(null);
  const [dimensions] = useState({ width: 700, height: 420 });
  const padding = 70;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/dreams/personal-map`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch {
        setData({ themes: [], emotions: [], connections: [], total_dreams: 0 });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const nodes = useMemo(() => {
    if (!data) return [];
    return layoutNodes(data.themes, data.emotions, dimensions.width, dimensions.height, padding);
  }, [data, dimensions]);

  const nodeMap = useMemo(() => {
    const map = {};
    nodes.forEach((n) => { map[n.id] = n; });
    return map;
  }, [nodes]);

  const maxCount = useMemo(() => {
    if (!nodes.length) return 1;
    return Math.max(...nodes.map((n) => n.count), 1);
  }, [nodes]);

  const activeConnections = useMemo(() => {
    if (!activeNode || !data) return new Set();
    const set = new Set();
    data.connections.forEach((c) => {
      if (c.from === activeNode || c.to === activeNode) {
        set.add(c.from);
        set.add(c.to);
      }
    });
    return set;
  }, [activeNode, data]);

  const handleNodeEnter = useCallback((id) => setActiveNode(id), []);
  const handleNodeLeave = useCallback(() => setActiveNode(null), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || data.total_dreams === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        </div>
        <p className="text-purple-200/60 font-body text-sm">
          {t("personalMap.empty") || "Kirjoita muutama uni niin henkilökohtainen unikarttasi alkaa muodostua."}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="personal-dream-map">
      <div className="flex items-center justify-between mb-3">
        <p className="text-purple-200/50 font-body text-xs">
          {t("personalMap.basedOn") || "Perustuu"} {data.total_dreams} {t("personalMap.dreams") || "uneen"}
        </p>
      </div>

      <svg
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        className="w-full h-auto"
        style={{ maxHeight: "420px" }}
      >
        <defs>
          <radialGradient id="pmBg" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#1a1040" />
            <stop offset="100%" stopColor="#0a0820" />
          </radialGradient>
          <radialGradient id="pmThemeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pmEmotionGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#6ee7b7" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#6ee7b7" stopOpacity="0" />
          </radialGradient>
          <filter id="pmGlow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <rect width={dimensions.width} height={dimensions.height} rx="16" fill="url(#pmBg)" />

        {/* Stars */}
        {STARS.map((s, i) => (
          <circle key={`s${i}`} cx={(s.x / 100) * dimensions.width} cy={(s.y / 100) * dimensions.height} r={s.r} fill="white" opacity={s.opacity}>
            <animate attributeName="opacity" values={`${s.opacity};${s.opacity * 0.2};${s.opacity}`} dur={`${3 + s.delay}s`} repeatCount="indefinite" />
          </circle>
        ))}

        {/* Connections */}
        {data.connections.map((conn, i) => {
          const from = nodeMap[conn.from];
          const to = nodeMap[conn.to];
          if (!from || !to) return null;
          const isHighlighted = activeNode && (conn.from === activeNode || conn.to === activeNode);
          const isDimmed = activeNode && !isHighlighted;
          const maxStrength = Math.max(...data.connections.map((c) => c.strength), 1);
          const normalizedStrength = conn.strength / maxStrength;

          return (
            <line
              key={`c${i}`}
              x1={from.x} y1={from.y} x2={to.x} y2={to.y}
              stroke={isHighlighted ? "#c4b5fd" : "#4c3a8a"}
              strokeWidth={isHighlighted ? 1.5 + normalizedStrength * 2 : 0.5 + normalizedStrength}
              strokeOpacity={isDimmed ? 0.05 : isHighlighted ? 0.7 : 0.15}
              strokeDasharray={isHighlighted ? "none" : "3 3"}
              style={{ transition: "all 0.4s ease" }}
            />
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => {
          const isTheme = node.type === "theme";
          const isActive = activeNode === node.id;
          const isConnected = activeConnections.has(node.id);
          const isDimmed = activeNode && !isConnected;
          const sizeScale = 0.5 + (node.count / maxCount) * 0.5;
          const baseR = isTheme ? 16 : 12;
          const r = baseR * sizeScale * (isActive ? 1.2 : 1);
          const label = t(`dreamMap.${node.id}`) !== `dreamMap.${node.id}` ? t(`dreamMap.${node.id}`) : node.id;

          return (
            <g
              key={node.id}
              onMouseEnter={() => handleNodeEnter(node.id)}
              onMouseLeave={handleNodeLeave}
              onClick={() => setActiveNode((p) => (p === node.id ? null : node.id))}
              style={{ cursor: "pointer", transition: "opacity 0.4s" }}
              opacity={isDimmed ? 0.15 : 1}
            >
              {(isActive || isConnected) && (
                <circle cx={node.x} cy={node.y} r={r + 10} fill={isTheme ? "url(#pmThemeGlow)" : "url(#pmEmotionGlow)"} />
              )}
              <circle
                cx={node.x} cy={node.y} r={r}
                fill={isTheme ? (isActive ? "#a78bfa" : "#7c3aed") : (isActive ? "#6ee7b7" : "#10b981")}
                stroke={isActive ? "white" : isTheme ? "#c4b5fd" : "#a7f3d0"}
                strokeWidth={isActive ? 2 : 0.8}
                filter={isActive ? "url(#pmGlow)" : "none"}
                style={{ transition: "all 0.3s" }}
              />
              {/* Count inside node */}
              <text x={node.x} y={node.y + 1} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="9" fontWeight="600" opacity={0.8} style={{ userSelect: "none" }}>
                {node.count}
              </text>
              {/* Label */}
              <text
                x={node.x} y={node.y + r + 13}
                textAnchor="middle" fill={isActive || isConnected ? "white" : "#a5b4c8"}
                fontSize={isTheme ? "10" : "9"} fontFamily="system-ui, sans-serif"
                fontWeight={isActive ? "600" : "400"}
                style={{ transition: "fill 0.3s", userSelect: "none" }}
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex justify-center gap-5 mt-3">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
          <span className="text-xs text-purple-200/50 font-body">{t("dreamMap.themes") || "Teemat"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span className="text-xs text-purple-200/50 font-body">{t("dreamMap.emotions") || "Tunteet"}</span>
        </div>
      </div>
    </div>
  );
}
